import { inject, Injectable, signal } from '@angular/core'
import { firstValueFrom } from 'rxjs'
import { ExternalFormsService } from 'superlikers-microsites-api'
import { LocalStorageService } from '../local-storage/local-storage.service'
import { Participant } from '@participant/models/participant'

interface CacheItem<T> {
  data: T
  expiryTimestamp: number
  serverTimestamp: number
}

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  externalFormsService = inject(ExternalFormsService)
  localStorage = inject(LocalStorageService)

  // 1 semana = 7 días * 24 horas * 60 minutos = 10,080 minutos
  ttlMinutes = signal(10080)

  serverTimestamp = signal<number | null>(null)
  expiryTimestamp = signal(new Date().getTime() + this.ttlMinutes() * 60 * 1000)

  hasInitialized = signal(false)
  currentUserUid = signal<string | null>(null)

  async init(currentUser: Participant | null) {
    // Store current user uid
    this.currentUserUid.set(currentUser?.uid ?? null)

    // Force reload if the app hasn't been refreshed in over a week
    const lastReload = this.localStorage.getItem('last_reload_timestamp')
    const now = new Date().getTime()

    if (!lastReload) {
      this.localStorage.setItem('last_reload_timestamp', now.toString())
    } else if (now - parseInt(lastReload) > 24 * 60 * 60 * 1000) {
      this.localStorage.setItem('last_reload_timestamp', now.toString())
      window.location.reload()
      return
    }

    // Check if server timestamp has changed to clear cache data
    const newTimestamp = await this.getServerTimestamp()
    if (!newTimestamp) return

    const storedTimestamp = this.localStorage.getItem('cache_server_timestamp')

    if (storedTimestamp) {
      const oldTimestamp = parseInt(storedTimestamp)

      // If server timestamp is newer, clear all cache
      if (newTimestamp > oldTimestamp) {
        console.log('Server timestamp updated - clearing all cache')
        this.clear()
      }
    }

    // Store new timestamp and set signal
    this.localStorage.setItem('cache_server_timestamp', newTimestamp.toString())
    this.serverTimestamp.set(newTimestamp)
  }

  /**
   * Genera una key única por usuario
   * Formato: uid_key (ej: "user123_big5_resume_enero_2026")
   */
  private getUserKey(key: string): string {
    const uid = this.currentUserUid()
    if (!uid) {
      console.warn('CacheService: No user uid available')
      return key
    }
    return `${uid}_${key}`
  }

  setItem<T>(key: string, data: T): void {
    const serverTimestamp = this.serverTimestamp()
    if (!serverTimestamp) return

    const cacheItem: CacheItem<T> = {
      data,
      expiryTimestamp: this.expiryTimestamp(),
      serverTimestamp
    }

    const userKey = this.getUserKey(key)
    this.localStorage.setItem(userKey, JSON.stringify(cacheItem))
  }

  getItem<T>(key: string): T | null {
    const userKey = this.getUserKey(key)
    const item = this.localStorage.getItem(userKey)

    if (!item) return null

    try {
      const cacheItem: CacheItem<T> = JSON.parse(item)

      const currentServerTime = this.serverTimestamp()
      if (!currentServerTime) return null

      if (currentServerTime > cacheItem.serverTimestamp) {
        this.removeItem(key)
        return null
      }

      const now = new Date().getTime()
      if (now > cacheItem.expiryTimestamp) {
        this.removeItem(key)
        return null
      }

      return cacheItem.data
    } catch {
      this.removeItem(key)
      return null
    }
  }

  removeItem(key: string): void {
    const userKey = this.getUserKey(key)
    this.localStorage.removeItem(userKey)
  }

  /**
   * Limpia todo el cache del usuario actual
   */
  clearUserCache(): void {
    const uid = this.currentUserUid()
    if (!uid) return

    const keys = Object.keys(localStorage)
    const userPrefix = `${uid}_`

    keys.forEach(key => {
      if (key.startsWith(userPrefix)) {
        this.localStorage.removeItem(key)
      }
    })

    console.log(`Cache cleared for user: ${uid}`)
  }

  /**
   * Limpia TODO el localStorage (todos los usuarios)
   */
  clear(): void {
    this.localStorage.clear()
  }

  private async getServerTimestamp(): Promise<number | null> {
    const list$ = this.externalFormsService.getExternalsFormList({ page: 1, category: 'cache' })
    const list = await firstValueFrom(list$)

    if (list.data.length === 0) return null

    const fields$ = this.externalFormsService.getExternalsFormFields(list.data[0]._id)
    const fields = await firstValueFrom(fields$)

    const field = fields.object.fields.find(field => field.name === 'time')?.default_value ?? ''

    const date = new Date(field)
    return date.getTime()
  }
}
